import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { Search, Filter, ArrowUpRight, ArrowDownLeft, ArrowLeftRight, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useTheme } from '../context/ThemeContext';

export default function Transactions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [categories, setCategories] = useState([]);
  const { tableStyle } = useTheme();

  // Filtros
  const [type, setType] = useState(searchParams.get('type') || '');
  const [walletId, setWalletId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const params = { page, per_page: 20 };
    if (type) params.type = type;
    if (walletId) params.wallet_id = walletId;
    if (categoryId) params.category_id = categoryId;
    if (search) params.search = search;
    const res = await api.get('/transactions', { params });
    setTransactions(res.data.data);
    setPagination({ current_page: res.data.current_page, last_page: res.data.last_page, total: res.data.total });
    setLoading(false);
  }, [page, type, walletId, categoryId, search]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  useEffect(() => {
    api.get('/wallets').then(r => setWallets(r.data));
    api.get('/categories').then(r => setCategories(r.data));
  }, []);

  const handleDelete = async (id) => {
    if (confirm('Eliminar esta transação?')) {
      await api.delete(`/transactions/${id}`);
      fetchTransactions();
    }
  };

  const goPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p);
    setSearchParams(params);
  };

  // Agrupar por data
  const groups = transactions.reduce((acc, tx) => {
    const dateKey = format(new Date(tx.date), 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(tx);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Transações</h1>
          <p className="page-subtitle">{pagination.total} transações encontradas</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/transactions/new')}>
          + Nova Transação
        </button>
      </div>

      {/* Filtros */}
      <div className="filter-bar">
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Pesquisar..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchTransactions()} />
        </div>
        {['', 'income', 'expense', 'transfer'].map((t) => (
          <button key={t} className={`filter-chip ${type === t ? 'active' : ''}`}
            onClick={() => { setType(t); goPage(1); }}>
            {t === '' ? 'Todas' : t === 'income' ? 'Receitas' : t === 'expense' ? 'Despesas' : 'Transferências'}
          </button>
        ))}
        <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(!showFilters)}>
          <Filter size={14} /> Filtros
        </button>
      </div>

      {showFilters && (
        <div className="card mb-md" style={{ padding: 16 }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Carteira</label>
              <select className="form-input form-select" value={walletId} onChange={e => { setWalletId(e.target.value); goPage(1); }}>
                <option value="">Todas</option>
                {wallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Categoria</label>
              <select className="form-input form-select" value={categoryId} onChange={e => { setCategoryId(e.target.value); goPage(1); }}>
                <option value="">Todas</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className={`card table-${tableStyle}`}>
        {loading ? (
          [1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton" style={{ height: 56, marginBottom: 8, borderRadius: 12 }} />)
        ) : Object.keys(groups).length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-title">Sem transações</p>
            <p className="empty-state-text">Crie a sua primeira transação usando o botão +</p>
          </div>
        ) : (
          Object.entries(groups).map(([dateKey, group]) => (
            <div key={dateKey}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', padding: '12px 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {format(new Date(dateKey), "EEEE, d 'de' MMMM", { locale: pt })}
              </div>
              {group.map((tx) => {
                const isExpense = tx.type === 'expense';
                const isTransfer = tx.type === 'transfer';
                const Icon = isExpense ? ArrowUpRight : isTransfer ? ArrowLeftRight : ArrowDownLeft;
                return (
                  <div key={tx.id} className="transaction-item" onClick={() => navigate(`/transactions/${tx.id}/edit`)}>
                    <div className="transaction-icon" style={{
                      background: isExpense ? 'var(--accent-red-dim)' : isTransfer ? 'var(--accent-purple-dim)' : 'var(--accent-green-dim)',
                    }}>
                      <Icon size={20} style={{ color: isExpense ? 'var(--accent-red)' : isTransfer ? 'var(--accent-purple)' : 'var(--accent-green)' }} />
                    </div>
                    <div className="transaction-info">
                      <div className="transaction-desc">{tx.description || tx.category?.name || 'Sem descrição'}</div>
                      <div className="transaction-meta">
                        <span>{tx.category?.name}</span>
                        <span>·</span>
                        <span>{tx.wallet?.name}</span>
                        {tx.is_pending && <span className="badge badge-pending">Pendente</span>}
                      </div>
                    </div>
                    <div className={`transaction-amount ${tx.type}`}>
                      {isExpense ? '-' : '+'}{formatCurrency(tx.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Paginação */}
      {pagination.last_page > 1 && (
        <div className="pagination">
          <button className="pagination-btn" disabled={page <= 1} onClick={() => goPage(page - 1)}>
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: Math.min(pagination.last_page, 7) }, (_, i) => i + 1).map(p => (
            <button key={p} className={`pagination-btn ${page === p ? 'active' : ''}`} onClick={() => goPage(p)}>
              {p}
            </button>
          ))}
          <button className="pagination-btn" disabled={page >= pagination.last_page} onClick={() => goPage(page + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
